<script>
import { mapActions, mapState } from 'pinia';

import { useUserStore } from '../../../store/useUserStore.js';
import { useFeedStore } from '../../../store/useFeedStore.js';
import { createLobby } from '../api/lobbying';
import { AiEnhancementPositions } from '@resources/js/constants';

export default {
  name: 'CpLobbyFormDialog',
  directives: {
    margin: require('../directives/margins').margin,
    marginTop: require('../directives/margins').marginTop,
    marginBottom: require('../directives/margins').marginBottom,
  },
  components: {
    CpButton: require('../button').default,
    CpButtonLink: require('../button-link').default,
    CpDialog: require('../dialog').default,
    CpBoxContent: require('../box-content').default,
    CpBox: require('../box').default,
    CpTextInput: require('../inputs/text').default,
    EndorseLobbyIcon: require('../social-feed/icons/endorse-lobby').default,
    OpposeLobbyIcon: require('../social-feed/icons/oppose-lobby').default,
    CpBaseSvgIcon: require('../base-svg-icon').default,
    EndorseIcon: require('../social-feed/icons/endorse').default,
    CpDialogAuthForm: require('../auth-form/dialog-auth-form').default,
    CpTooltip: require('../tooltip').default,
    CpTooltipToggle: require('../tooltip-toggle').default,
    CpSelect: require('../select').default,
    CpVerifyEmailDialog: require('./VerifyEmail').default,
    CpRepresentivesToSelect: require('../lobbying/RepresentivesToSelect').default,
    CpAiMessageEnhancer: require('@resources/js/components/AiMessageEnhancer').default,
    CpMessageAsComment: require('@resources/js/lobbying/lobbyForm/MessageAsComment').default,
  },
  props: {
    politician: { type: Object, default: null },
    showDialog: { type: Boolean, default: false },
    type: { type: String, required: true }, // 'endorse' or 'oppose' or 'none'
    modalFeed: { type: Boolean, default: false },
    refProp: { type: Object, default: null },
    callback: { type: Function, default: () => {} },
    newsArticle: { type: Object, default: null },
    commentToRep: { type: String, default: null },
    shareMessageInComments: { type: Boolean, default: false },
    originalPostType: { type: String, default: null },
    originalPostId: { type: [Number, String], default: null },
  },
  created() {
    this.currentUrl = window.location.href;
  },
  async mounted() {
    if (this.isUserLogin && !this.userProfileData) {
      await this.getUserProfile();
    }

    if (this.userProfileData) {
      this.address = this.userProfileData?.address_street || '';
      this.city = this.userProfileData?.address_city || '';
      this.state = this.userProfileData?.address_state || '';
      this.postalCode = this.userProfileData?.address_zip || '';
      this.phone = this.userProfileData?.phone || '';
      this.honorific = this.userProfileData?.honorific || '';
    }

    if (!Object.keys(this.usStates).length) {
      this.getStatesFromStore();
    }

    this.setDefaultEmailBody();
    this.setDefaultEmailSubject();
  },
  data() {
    return {
      cc: [],
      loading: false,
      lobbySuccess: false,
      lobbyError: false,
      emailSubject: '',
      emailBody: '',
      originalEmailBody: '',
      isBodyChecked: false,
      errorText: '',
      ccEmail: '',
      emailError: false,
      address: '',
      city: '',
      state: '',
      postalCode: '',
      phone: '',
      phoneError: '',
      zipError: '',
      addressError: '',
      cityError: '',
      stateError: '',
      subjectError: '',
      bodyTextError: '',
      showVerifyEmailModal: false,
      showAuthModal: false,
      currentUrl: null,
      selectedRepresentativeId: null,
      selectedRepresentative: null,
      isSharingMessage: false,
      honorific: '',
      honorificError: '',
      honorifics: [{ value: 'Mr.' }, { value: 'Mrs.' }, { value: 'Miss.' }, { value: 'Ms.' }, { value: 'Dr.' }],
      messageGreeting: '',
      messageBody: '',
      messageClosing: '',
      lobbyId: null,
    };
  },
  methods: {
    setDefaultEmailBody() {
      if (this.userProfileData) {
        const representativeName = this.selectedRepresentative?.name || 'Representative';
        let parts = null;

        if (this.type === 'none' && this.newsArticle) {
          parts = this.lobbyBodyParts(this.userProfileData, this.type, representativeName, this.newsArticle);
        } else if (this.type === 'none' && this.commentToRep) {
          parts = this.lobbyBodyParts(this.userProfileData, this.type, representativeName, null, this.commentToRep);
        } else if (this.type === 'none' && this.politician) {
          parts = this.lobbyBodyParts(this.userProfileData, this.type, this.politician.full_name);
        } else if (this.politician?.full_name && this.type && !this.emailBody) {
          parts = this.lobbyBodyParts(this.userProfileData, this.type, this.politician.full_name);
        }

        if (parts) {
          this.messageGreeting = parts.greeting;
          this.messageBody = parts.body;
          this.messageClosing = parts.closing;
          this.emailBody = this.constructFullMessage();
          this.originalEmailBody = this.emailBody;
        }
      }
    },
    setDefaultEmailSubject() {
      if (!this.emailSubject) {
        this.emailSubject = this.subject;
      }
    },
    lobby() {
      this.loading = true;

      const politicianId = this.type === 'none' && !this.politician ? this.selectedRepresentativeId : this.politician.id;

      if (this.type === 'none' && !politicianId) {
        this.errorText = 'Please select a representative to contact.';
        this.loading = false;
        return;
      }

      const dataToSend = {
        source_name: this.newsArticle ? `CrowdBlue | Web | ${this.newsArticle.title}` : 'CrowdBlue | Web',
        source_url: this.newsArticle ? this.newsArticle.permalink : this.currentUrl,
        update_profile: this.updateProfileFields,
        address_street: this.address,
        address_city: this.city,
        address_state: this.state,
        address_zip: this.postalCode,
        phone: this.phone,
        name_prefix: this.honorific,
        military_service_branch: '',
        military_rank: '',
        organization: '',
        type: this.type,
        subject: this.emailSubject || this.subject,
        body: this.emailBody,
        cc: this.cc,
        modalFeed: this.modalFeed,
      };

      if (!this.honorific) {
        this.honorificError = 'Please select an honorific';
        this.loading = false;
        return;
      }
      if (!this.isBodyChecked && this.originalEmailBody === this.emailBody) {
        this.isBodyChecked = true;
        this.errorText = 'Your representative requests a detailed message expressing your views.';
        this.loading = false;
      } else {
        this.errorText = '';
        createLobby(politicianId, dataToSend)
          .then(async res => {
            this.loading = false;
            if (this.updateProfileFields) {
              await this.getUserProfile();
            }
            if (this.refProp) {
              this.refProp.reloadFromStart();
            }
            if (this.shareMessageInComments) {
              this.lobbyId = res.lobby.id;
              this.isSharingMessage = true;
            } else {
              this.lobbySuccess = true;
              setTimeout(() => {
                this.lobbySuccess = false;
                this.handleDialog();
              }, 3000);
              if (this.callback) {
                this.callback();
              }
            }
          })
          .catch(error => {
            this.loading = false;
            this.lobbyError = true;
            console.warn(error);
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    addCc(email) {
      if (email.trim() !== '') {
        this.cc.push(email);
        this.emailError = false;
        this.ccEmail = '';
      } else {
        this.emailError = true;
      }
    },
    deleteCc(index) {
      this.cc.splice(index, 1);
    },
    handleVerifyEmailModal() {
      this.showVerifyEmailModal = !this.showVerifyEmailModal;
    },
    handleDialog() {
      this.$emit('close');
      this.isBodyChecked = false;
      this.errorText = '';
    },
    handleStartLobby() {
      this.showAuthModal = !this.showAuthModal;
    },
    handlePhoneBlur() {
      const formatted = this.formatPhone(this.phone);
      if (formatted !== this.phone) {
        this.phone = formatted;
      }
      this.phoneError = this.validatePhone(this.phone);
    },
    handleRepresentativeSelected(representativeId, representative) {
      this.selectedRepresentativeId = representativeId;
      this.selectedRepresentative = representative;
    },
    constructFullMessage() {
      const parts = [];

      if (this.messageGreeting) {
        parts.push(this.messageGreeting);
      }

      if (this.messageBody) {
        parts.push(this.messageBody);
      }

      if (this.messageClosing) {
        parts.push(this.messageClosing);
      }

      return parts.join('\n\n');
    },
    handleMessageEnhanced(enhancedBody) {
      this.messageBody = enhancedBody;
      this.emailBody = this.constructFullMessage();
    },
    ...mapActions(useUserStore, ['getUserProfile']),
    ...mapActions(useFeedStore, ['getStatesFromStore']),
  },
  computed: {
    currentMessageBody() {
      if (!this.emailBody || !this.messageGreeting || !this.messageClosing) {
        return this.emailBody || this.messageBody;
      }
      let body = this.emailBody;
      if (this.messageGreeting) {
        body = body.replace(this.messageGreeting, '');
      }
      if (this.messageClosing) {
        const closingEscaped = this.messageClosing.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        body = body.replace(new RegExp(closingEscaped + '$'), '');
      }

      return body.trim();
    },
    enhancementPosition() {
      return this.type === 'oppose' ? AiEnhancementPositions.AGAINST : AiEnhancementPositions.FAVOR;
    },
    allProfileFieldsCompleted() {
      return this.address.trim() && this.city.trim() && this.state.trim() && this.postalCode.trim() && this.phone.trim();
    },
    profileNeedsModification() {
      return (
        !this.userProfileData?.address_street ||
        !this.userProfileData?.address_city ||
        !this.userProfileData?.address_state ||
        !this.userProfileData?.address_zip ||
        !this.userProfileData?.phone
      );
    },
    updateProfileFields() {
      return this.profileNeedsModification || !this.userProfileData?.honorific;
    },
    hasEmailBody() {
      return !!this.emailBody.trim();
    },
    hasCustomMessage() {
      return !this.emailBody.includes('WRITE YOUR MESSAGE HERE');
    },
    isRepresentativeSelected() {
      return this.type !== 'none' || (this.type === 'none' && this.politician) || this.selectedRepresentativeId !== null;
    },
    hasInputError() {
      return !!this.phoneError || !!this.zipError || !!this.addressError || !!this.cityError || !!this.stateError || !!this.subjectError;
    },
    hasHonorififError() {
      return this.honorificError !== '';
    },
    subject() {
      return this.type === 'none' ? 'A message from your Constituent' : this.lobbySubject(this.type, this.politician?.full_name);
    },
    selectedRepresentativeName() {
      if (this.type === 'none' && this.selectedRepresentative) {
        return this.selectedRepresentative.full_name || this.selectedRepresentative.name;
      }
      return this.politician?.full_name;
    },
    ...mapState(useUserStore, ['isUserLogin', 'userProfileData']),
    ...mapState(useFeedStore, ['usStates']),
  },
  watch: {
    phone() {
      this.phoneError = this.validatePhone(this.phone);
    },
    postalCode() {
      this.zipError = this.validateZip(this.postalCode);
    },
    address() {
      this.addressError = this.validateInput(this.address);
    },
    city() {
      this.cityError = this.validateInput(this.city);
    },
    state() {
      this.stateError = this.validateInput(this.state);
    },
    emailSubject() {
      this.subjectError = this.validateInput(this.emailSubject);
    },
    honorific() {
      this.honorificError = this.honorific ? '' : 'Please select an honorific';
    },
    userProfileData: 'setDefaultEmailBody',
    type: 'setDefaultEmailBody',
    'politician.full_name': 'setDefaultEmailBody',
    selectedRepresentative: 'setDefaultEmailBody',
    newsArticle: 'setDefaultEmailBody',
    subject: 'setDefaultEmailSubject',
    emailBody() {
      if (this.isRepresentativeSelected && !this.hasCustomMessage) {
        this.bodyTextError = 'Please personalize your message to your representative.';
      } else {
        this.bodyTextError = '';
      }
    },
  },
};
</script>
<template>
  <div>
    <CpDialog v-if="showDialog" :isNested="true">
      <CpBox>
        <CpBoxContent>
          <section style="padding: 0 2em; max-width: 60ch; overflow: auto; max-height: 90vh">
            <template v-if="!lobbySuccess && !lobbyError && !isSharingMessage" class="share-container">
              <div v-if="type !== 'none'" class="icon-lobby">
                <CpBaseSvgIcon :width="60" :height="60" :viewBox="'0 0 20 20'" :icon-color="'none'" :stroke-color="'none'">
                  <EndorseLobbyIcon v-if="type == 'endorse'" />
                  <OpposeLobbyIcon v-else-if="type == 'oppose'" />
                </CpBaseSvgIcon>
              </div>
              <h2 v-if="type !== 'none' || (type === 'none' && politician)" class="headline black-color text-center">How your email will look</h2>
              <CpRepresentivesToSelect v-if="type === 'none' && !politician" @representative-selected="handleRepresentativeSelected" v-margin="1" />
              <div style="border: #eee 1px solid; padding: 10px">
                <div class="box-titles">
                  <h4 class="subheadline1 neutral5-color">Subject</h4>
                  <CpTextInput block v-model="emailSubject" :error="subjectError" placeholder="Enter your subject line" />
                  <div v-if="!userProfileData?.honorific" v-margin="0.5">
                    <h4 class="subheadline1 neutral5-color">Choose your title <span class="caption2 primary-color">(Required)</span></h4>
                    <CpSelect block v-model="honorific">
                      <option value="">- Select title -</option>
                      <option v-for="{ value } in honorifics" :value="value">{{ value }}</option>
                    </CpSelect>
                    <p v-if="honorificError" class="footnote error-color">{{ honorificError }}</p>
                  </div>
                  <h4 class="subheadline1 neutral5-color">Body</h4>
                  <CpTextInput multiline block v-model="emailBody" :rows="8" :error="bodyTextError" />
                  <CpAiMessageEnhancer
                    :message="currentMessageBody"
                    :sourceText="newsArticle?.content || null"
                    :disabled="loading"
                    :position="enhancementPosition"
                    @message-enhanced="handleMessageEnhanced"
                  />
                </div>
                <h5 class="subheadline1 neutral5-color">Share this with friends and colleagues!</h5>
                <div v-if="cc.length" class="box-cc-list">
                  <div v-for="(item, index) in cc" class="text-email">
                    <h5 style="margin: 0">
                      {{ item }}
                    </h5>
                    <i @click="deleteCc(index)" class="fas fa-times" style="margin-left: 5px; cursor: pointer"></i>
                  </div>
                </div>
                <div class="add-field-container">
                  <CpTextInput
                    block
                    type="text"
                    v-model="ccEmail"
                    :disabled="loading"
                    aria-label="Search"
                    :class="emailError ? 'input-error' : ''"
                    placeholder="CC"
                  >
                  </CpTextInput>
                  <cp-button thin @click="addCc(ccEmail)" class="btn-add">ADD</cp-button>
                </div>
              </div>
              <div
                v-if="profileNeedsModification"
                :class="['needsModification', { 'needsModification--completed': allProfileFieldsCompleted && !hasInputError }]"
              >
                <h4 class="subheadline1 black-color">You need to complete this information</h4>
                <div v-if="!userProfileData?.address_street" v-margin-bottom="0.5">
                  <label class="subheadline1 neutral5-color">
                    Address street <span class="caption2 primary-color">(Required)</span>
                    <CpTextInput block v-model="address" placeholder="Address street" :error="addressError" />
                  </label>
                </div>
                <div v-if="!userProfileData?.address_city" v-margin-bottom="0.5">
                  <label class="subheadline1 neutral5-color">
                    City <span class="caption2 primary-color">(Required)</span>
                    <CpTextInput block v-model="city" placeholder="City" :error="cityError" />
                  </label>
                </div>
                <div v-if="!userProfileData?.address_state" v-margin-bottom="0.5">
                  <label class="subheadline1 neutral5-color">
                    State <span class="caption2 primary-color">(Required)</span>
                    <CpSelect block v-model="state">
                      <option value="">- Select State -</option>
                      <option v-for="(stateName, stateAbbrev) in usStates" :value="stateAbbrev">{{ stateName }}</option>
                    </CpSelect>
                  </label>
                  <p v-if="stateError" class="footnote error-color">{{ stateError }}</p>
                </div>
                <div v-if="!userProfileData?.address_zip" v-margin-bottom="0.5">
                  <label class="subheadline1 neutral5-color">
                    Postal code <span class="caption2 primary-color">(Required)</span>
                    <CpTextInput block v-model="postalCode" placeholder="Postal code" :error="zipError" />
                  </label>
                </div>
                <div v-if="!userProfileData?.phone" v-margin-bottom="0.5">
                  <label class="subheadline1 neutral5-color">
                    Phone number <span class="caption2 primary-color">(Required)</span>
                    <CpTextInput block v-model="phone" placeholder="(###) ###-#### or ###-###-####" @blur="handlePhoneBlur" :error="phoneError" />
                  </label>
                </div>
              </div>
              <p v-if="errorText" class="footnote error-color">{{ errorText }}</p>
              <div class="box-buttons">
                <CpButton @click="handleDialog" class="btn-lobby" block>CANCEL</CpButton>
                <CpButton
                  @click="lobby"
                  class="btn-lobby"
                  block
                  primary
                  :loading="loading"
                  :disabled="
                    loading ||
                    !allProfileFieldsCompleted ||
                    hasInputError ||
                    !hasEmailBody ||
                    !isRepresentativeSelected ||
                    !hasCustomMessage ||
                    hasHonorififError
                  "
                  >SEND</CpButton
                >
              </div>
            </template>
            <template v-else-if="isSharingMessage">
              <CpMessageAsComment
                :emailBody="emailBody"
                :lobbyId="lobbyId"
                :postId="originalPostId"
                :postType="originalPostType"
                :handleDialog="handleDialog"
              />
            </template>
            <template v-else-if="lobbySuccess">
              <div style="display: flex; justify-content: center">
                <i class="fas fa-check share-icon"></i>
              </div>
              <p class="heading-4">Success, {{ userProfileData?.name }}! Your constituent message has been sent to {{ selectedRepresentativeName }}</p>
            </template>
            <template v-else-if="lobbyError">
              <svg
                @click.prevent="handleDialog"
                class="close-svg-icon"
                stroke="currentColor"
                fill="currentColor"
                stroke-width="0"
                viewBox="0 0 512 512"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M278.6 256l68.2-68.2c6.2-6.2 6.2-16.4 0-22.6-6.2-6.2-16.4-6.2-22.6 0L256 233.4l-68.2-68.2c-6.2-6.2-16.4-6.2-22.6 0-3.1 3.1-4.7 7.2-4.7 11.3 0 4.1 1.6 8.2 4.7 11.3l68.2 68.2-68.2 68.2c-3.1 3.1-4.7 7.2-4.7 11.3 0 4.1 1.6 8.2 4.7 11.3 6.2 6.2 16.4 6.2 22.6 0l68.2-68.2 68.2 68.2c6.2 6.2 16.4 6.2 22.6 0 6.2-6.2 6.2-16.4 0-22.6L278.6 256z"
                ></path>
              </svg>
              <div style="display: flex; justify-content: center">
                <i class="fas fa-times error-icon"></i>
              </div>
              <p class="heading-4" style="text-align: center">An error has occurred!</p>
            </template>
          </section>
        </CpBoxContent>
      </CpBox>
    </CpDialog>
    <CpVerifyEmailDialog v-if="showVerifyEmailModal" :handleDialog="handleVerifyEmailModal" />
    <CpDialogAuthForm v-if="showAuthModal" :handleCloseButton="handleStartLobby" :callback-action="() => {}" />
  </div>
</template>
<style scoped>
.input-error {
  border-bottom: 3px solid red;
}
.text-email {
  margin: 5px 5px 5px 0;
  border-radius: 5px;
  padding: 5px;
  background: #ffe3da;
  color: #ed572f;
  display: flex;
  align-items: center;
}
.box-cc-list {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 15px;
}
.btn-add {
  margin-left: 5px;
}
.text-input__container--block {
  width: 100%;
  border-radius: 10px;
}
.text-cc-email {
  margin: 0 5px 0 0;
}
.box-cc-email {
  background-color: #d6d6d6;
  padding: 5px 10px;
  display: flex;
  align-items: center;
}
.subtitle {
  font-weight: normal;
}
.icon-lobby {
  text-align: center;
}
.box-titles {
  padding: 0 10px;
}
.btn-lobby {
  width: 30%;
  margin: 5px;
}
.box-buttons {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
.btn-share span.button__content {
  padding: 0;
}
.comment-input div input.text-input:focus {
  outline: none !important;
}
.comment-input div input.text-input {
  border: 0;
  color: var(--neutral5);
  flex-grow: 1;
  font: inherit;
  width: auto;
  padding: 0.5em;
  margin-bottom: 0;
}
.share-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1000px;
  margin: auto;
}
.share-icon {
  font-size: 3em;
  color: var(--success);
}
.error-icon {
  font-size: 3em;
  color: red;
}
div.icon-wrapper {
  width: 100%;
  height: 100%;
  flex-direction: row;
  align-content: center;
  align-items: center;
  background-image: none !important;
  margin-top: 0px;
}
svg.share-svg-icon {
  height: 1em;
  width: 1em;
}
div.network-text-title {
  font-size: 10px;
  font-weight: 700;
  padding-bottom: 5px;
}
svg.close-svg-icon {
  float: right;
  top: 5px;
  right: 5px;
  position: absolute;
  height: 2.5em;
  width: 2.5em;
  cursor: pointer;
}
div.feed svg {
  color: #555555 !important;
  height: 1.5em !important;
  width: 1.5em !important;
}
.tooltipOption {
  display: flex;
  align-items: center;
  padding: 8px;
}
.tooltipIcon {
  margin-right: 10px;
}
.default-field {
  background-color: var(--white);
  border: 1px solid var(--neutral3);
  border-radius: 4px;
  padding: 8px 16px;
}
.add-field-container {
  display: flex;
}
.needsModification {
  border: 1px solid var(--error);
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.needsModification--completed {
  border-color: var(--neutral3);
}
.checkbox-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
@media only screen and (max-width: 768px) {
  .btn-lobby {
    width: 50%;
  }
  button.btn-add.button span.button__content {
    padding: 0.6em 0.4em;
  }
}
</style>
